import { supabase } from '@/integrations/supabase/client';
import type { EmpresaInfo, DocumentConfig } from './pdfDocumentEngine';

export interface BrandingBundle {
  empresa: EmpresaInfo | null;
  docConfig: Partial<DocumentConfig> | undefined;
}

const cache = new Map<string, BrandingBundle>();

export async function loadEmpresaYConfigParaPdf(empresaId?: string | null): Promise<BrandingBundle> {
  if (!empresaId) return { empresa: null, docConfig: undefined };
  if (cache.has(empresaId)) return cache.get(empresaId)!;

  const [empresaRes, configRes] = await Promise.all([
    supabase
      .from('empresas')
      .select('nombre, razon_social, nif_cif, direccion_calle, direccion_numero, codigo_postal, ciudad, provincia, telefono, email, logo_url, sello_url, numero_dgp, numero_reci')
      .eq('id', empresaId)
      .maybeSingle(),
    (supabase.from('documento_config' as any) as any)
      .select('color_primario, color_secundario, color_acento, mostrar_logo, mostrar_datos_empresa, texto_pie_pagina, texto_legal')
      .eq('empresa_id', empresaId)
      .maybeSingle(),
  ]);

  const empresa: EmpresaInfo | null = empresaRes.data
    ? {
        nombre: empresaRes.data.nombre,
        razon_social: empresaRes.data.razon_social,
        nif_cif: empresaRes.data.nif_cif,
        direccion_calle: empresaRes.data.direccion_calle,
        direccion_numero: empresaRes.data.direccion_numero,
        codigo_postal: empresaRes.data.codigo_postal,
        ciudad: empresaRes.data.ciudad,
        provincia: empresaRes.data.provincia,
        telefono: empresaRes.data.telefono,
        email: empresaRes.data.email,
        logo_url: empresaRes.data.logo_url,
        sello_url: (empresaRes.data as any).sello_url,
        numero_dgp: (empresaRes.data as any).numero_dgp,
        numero_reci: (empresaRes.data as any).numero_reci,
      }
    : null;

  const cfg = configRes.data as any;
  const docConfig: Partial<DocumentConfig> | undefined = cfg
    ? {
        colorPrimario: cfg.color_primario || undefined,
        colorSecundario: cfg.color_secundario || undefined,
        colorAcento: cfg.color_acento || undefined,
        mostrarLogo: cfg.mostrar_logo ?? true,
        mostrarDatosEmpresa: cfg.mostrar_datos_empresa ?? true,
        textoPiePagina: cfg.texto_pie_pagina || undefined,
        textoLegal: cfg.texto_legal || undefined,
      }
    : undefined;

  const bundle = { empresa, docConfig };
  cache.set(empresaId, bundle);
  return bundle;
}

export function clearBrandingCache(empresaId?: string) {
  if (empresaId) cache.delete(empresaId);
  else cache.clear();
}
